import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Printer, Receipt } from 'lucide-react';
import { get, post, qs } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { dateOnly, humanize, money, newIdempotencyKey } from '@/lib/utils';
import { printDocument } from '@/lib/print';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { DataTable, Dialog, PageHeader, SearchFilterBar } from '@/components/ui/layout';
import { Alert, Button, Checkbox, Field, Input, StatusBadge } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { toMinor, usePrintReceipt } from './receipt-print';

interface CreditCustomer {
  branchId: string;
  branchName: string | null;
  name: string;
  phone: string | null;
  openInvoices: number;
  netMinor: number;
  paidMinor: number;
  balanceMinor: number;
  oldestAt: string | null;
}

interface CustomerAccount {
  name: string;
  phone: string | null;
  balanceMinor: number;
  invoices: { id: string; number: string; context: string; status: string; createdAt: string; netMinor: number; paidMinor: number; balanceMinor: number }[];
}

interface SettledSale {
  id: string;
  number: string;
  amountMinor: number;
  balanceMinor: number;
}

function settlementPreview(invoices: readonly { id: string; number: string; balanceMinor: number }[], amountMinor: number): SettledSale[] {
  const rows: SettledSale[] = [];
  let left = amountMinor;
  for (const invoice of invoices) {
    if (left <= 0) break;
    const part = Math.min(left, Math.max(0, invoice.balanceMinor));
    if (part <= 0) continue;
    rows.push({ id: invoice.id, number: invoice.number, amountMinor: part, balanceMinor: invoice.balanceMinor });
    left -= part;
  }
  return rows;
}

interface OnAccountResult {
  paidMinor: number;
  changeMinor: number;
  remainingBalanceMinor: number;
  payments: { paymentId: string; receiptNo: string | null; invoiceId: string; invoiceNumber: string; amountMinor: number }[];
}

export function CustomerCreditPage() {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState<CreditCustomer | null>(null);

  const customers = useInfiniteQuery({
    queryKey: ['credit-customers', search],
    initialPageParam: '',
    queryFn: ({ pageParam }) =>
      get<{ data: CreditCustomer[]; nextCursor: string | null }>(`/customer-credit${qs({ q: search, limit: 50, cursor: pageParam })}`),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const rows = customers.data?.pages.flatMap((page) => page.data);
  const total = rows?.reduce((sum, row) => sum + row.balanceMinor, 0) ?? 0;

  return (
    <>
      <PageHeader title={tr('billing', 'credit_customers')} description={tr('billing', 'named_customers_who_took_goods_or_services_on')} />

      <SearchFilterBar
        search={{ value: search, onChange: setSearch, placeholder: tr('billing', 'customer_name') }}
        onReset={search ? () => setSearch('') : undefined}
      />

      <DataTable
        rows={rows}
        loading={customers.isLoading} refreshing={customers.isFetching}
        error={customers.error}
        rowKey={(row) => `${row.branchId}|${row.name}|${row.phone ?? ''}`}
        onRowClick={(row) => setOpen(row)}
        empty={tr('billing', 'no_customer_owes_anything_a_walk_in_sale_with_a')}
        footer={
          <>
            <span>
              {tr('billing', 'owing_on_this_list')} {money(total)}
            </span>
            {customers.hasNextPage && (
              <Button variant="outline" size="sm" onClick={() => void customers.fetchNextPage()} loading={customers.isFetchingNextPage}>
                {tr('billing', 'show_more')}
              </Button>
            )}
          </>
        }
        columns={[
          { header: tr('billing', 'customer'), sort: (row) => row.name, cell: (row) => <span className="font-medium">{row.name}</span> },
          { header: tr('billing', 'phone'), sort: (row) => row.phone, cell: (row) => row.phone ?? '—' },
          { header: tr('billing', 'branch'), sort: (row) => row.branchName, cell: (row) => row.branchName ?? '—' },
          { header: tr('billing', 'open_sales'), align: 'right', sort: (row) => row.openInvoices, cell: (row) => row.openInvoices },
          { header: tr('billing', 'oldest'), sort: (row) => row.oldestAt, cell: (row) => (row.oldestAt ? dateOnly(row.oldestAt) : '—') },
          { header: tr('billing', 'owing'), align: 'right', sort: (row) => row.balanceMinor, cell: (row) => <span className="font-semibold text-destructive">{money(row.balanceMinor)}</span> },
        ]}
      />

      {open && <CustomerAccountDialog customer={open} onClose={() => setOpen(null)} />}
    </>
  );
}

function CustomerAccountDialog({ customer, onClose }: { customer: CreditCustomer; onClose: () => void }) {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const printReceipt = usePrintReceipt();
  const [amount, setAmount] = useState('');
  const [mode, setMode] = useState('cash');
  const [tendered, setTendered] = useState('');
  const [txnRef, setTxnRef] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<OnAccountResult | null>(null);
  const [idempotencyKey, setIdempotencyKey] = useState(() => newIdempotencyKey());

  const [picked, setPicked] = useState<ReadonlySet<string>>(() => new Set());
  const [attempted, setAttempted] = useState(false);
  const needsPick = !customer.phone;

  const accountQuery = qs({ name: customer.name, phone: customer.phone ?? undefined, branchId: customer.branchId });
  const account = useQuery({
    queryKey: ['credit-customer', customer.branchId, customer.name, customer.phone],
    queryFn: () => get<CustomerAccount>(`/customer-credit/account${accountQuery}`),
  });

  const print = useMutation({
    mutationFn: () => get<{ html: string }>(`/customer-credit/account/print${accountQuery}`),
    onSuccess: (doc) => printDocument(doc.html),
    onError: (caught) => toast.error(tr('billing', 'could_not_print'), describeError(caught)),
  });

  const amountMinor = toMinor(amount);
  const tenderedMinor = tendered.trim() ? toMinor(tendered) : undefined;
  const owing = account.data?.balanceMinor ?? 0;
  const candidates = (account.data?.invoices ?? []).filter((invoice) => !needsPick || picked.has(invoice.id));
  const payable = candidates.reduce((sum, invoice) => sum + invoice.balanceMinor, 0);
  const amountProblem =
    needsPick && candidates.length === 0
      ? tr('billing', 'pick_at_least_one_sale')
      : !Number.isFinite(amountMinor)
        ? tr('billing', 'payment_amount_not_a_number')
        : amountMinor <= 0
          ? tr('billing', 'payment_amount_above_zero')
          : amountMinor > payable
            ? tr('billing', 'amount_above_owing_x', { owing: money(payable) })
            : null;
  const tenderedProblem =
    mode !== 'cash' || tenderedMinor === undefined
      ? null
      : !Number.isFinite(tenderedMinor)
        ? tr('billing', 'tendered_not_a_number')
        : !amountProblem && tenderedMinor < amountMinor
          ? tr('billing', 'tendered_less_than_amount_x', { short: money(amountMinor - tenderedMinor) })
          : null;
  const settles = amountProblem ? [] : settlementPreview(candidates, amountMinor);
  const showAmountProblem = attempted || amount.trim() !== '';

  const togglePick = (id: string, on: boolean) =>
    setPicked((current) => {
      const next = new Set(current);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });

  const collect = useMutation({
    mutationFn: () =>
      post<OnAccountResult>(
        '/customer-credit/collect',
        {
          customerName: customer.name,
          ...(customer.phone ? { customerPhone: customer.phone } : {}),
          branchId: customer.branchId,
          invoiceIds: settles.map((row) => row.id),
          amountMinor,
          mode,
          ...(mode === 'cash' && tenderedMinor !== undefined ? { tenderedMinor } : {}),
          ...(txnRef ? { txnRef } : {}),
        },
        { idempotencyKey },
      ),
    onSuccess: (done) => {
      toast.success(tr('billing', 'payment_on_account_recorded'), done.changeMinor > 0 ? tr('billing', 'change_due_x', { changeMinor: money(done.changeMinor) }) : undefined);
      setIdempotencyKey(newIdempotencyKey());
      setResult(done);
      setAmount('');
      setTendered('');
      setPicked(new Set());
      setAttempted(false);
      void queryClient.invalidateQueries({ queryKey: ['credit-customers'] });
      void queryClient.invalidateQueries({ queryKey: ['credit-customer'] });
      void queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
    onError: (caught) => setError(describeError(caught)),
  });

  return (
    <Dialog
      open
      wide
      onClose={onClose}
      title={customer.name}
      description={`${customer.phone ?? 'No phone on file'} · owes ${money(owing)}`}
      footer={
        <>
          <Button variant="outline" onClick={() => print.mutate()} loading={print.isPending}>
            <Printer className="h-4 w-4" /> {tr('billing', 'print_statement')}
          </Button>
          <Button variant="ghost" onClick={onClose}>
            {tr('billing', 'close')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <DataTable
          dense
          rows={account.data?.invoices}
          loading={account.isLoading} refreshing={account.isFetching}
          error={account.error}
          rowKey={(row) => row.id}
          empty={tr('billing', 'nothing_outstanding')}
          columns={[
            ...(needsPick && can('payment.collect')
              ? [
                  {
                    header: '',
                    cell: (row: CustomerAccount['invoices'][number]) => (
                      <Checkbox checked={picked.has(row.id)} onCheckedChange={(on) => togglePick(row.id, on)} aria-label={row.number} />
                    ),
                  },
                ]
              : []),
            {
              header: tr('billing', 'sale'),
              cell: (row) => (
                <Link className="underline" to={`/billing/invoices/${row.id}`}>
                  {row.number}
                </Link>
              ),
            },
            { header: tr('billing', 'date'), cell: (row) => dateOnly(row.createdAt) },
            { header: tr('billing', 'status'), cell: (row) => <StatusBadge status={row.status} /> },
            { header: tr('billing', 'amount'), align: 'right', cell: (row) => money(row.netMinor) },
            { header: tr('billing', 'paid'), align: 'right', cell: (row) => money(row.paidMinor) },
            { header: tr('billing', 'owing'), align: 'right', cell: (row) => <strong>{money(row.balanceMinor)}</strong> },
          ]}
        />

        {result && (
          <Alert variant="success" title={tr('billing', 'x_received', { paidMinor: money(result.paidMinor) })}>
            <div className="mt-1 space-y-1">
              {result.payments.map((payment) => (
                <div key={payment.paymentId} className="flex items-center justify-between gap-2 text-sm">
                  <span>
                    {payment.invoiceNumber}: {money(payment.amountMinor)}
                    {payment.receiptNo ? ` · receipt ${payment.receiptNo}` : ''}
                  </span>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => printReceipt.mutate({ kind: 'payment', id: payment.paymentId, size: 'thermal', original: true })}
                  >
                    <Receipt className="h-4 w-4" /> {tr('billing', 'receipt')}
                  </Button>
                </div>
              ))}
              {result.changeMinor > 0 && <p>{tr('billing', 'change_to_return')} {money(result.changeMinor)}</p>}
              <p>{tr('billing', 'still_owing')} {money(result.remainingBalanceMinor)}</p>
            </div>
          </Alert>
        )}

        {can('payment.collect') && owing > 0 && (
          <div className="space-y-3 rounded-md border p-3">
            <p className="text-sm font-medium">{tr('billing', 'take_a_payment_on_account')}</p>
            <p className="text-xs text-muted-foreground">{tr('billing', 'it_is_applied_to_the_oldest_open_sales_first')}</p>
            {needsPick && <Alert variant="warning">{tr('billing', 'no_phone_pick_sales')}</Alert>}
            <div className="grid gap-3 sm:grid-cols-3">
              <Field label={tr('billing', 'amount')} required error={showAmountProblem ? (amountProblem ?? undefined) : undefined}>
                <div className="flex gap-2">
                  <Input type="number" step="0.01" min={0} value={amount} onChange={(e) => setAmount(e.target.value)} className="tabular" />
                  <Button variant="outline" onClick={() => setAmount(String(payable / 100))} disabled={payable <= 0}>
                    {tr('billing', 'all')}
                  </Button>
                </div>
              </Field>
              <Field label={tr('billing', 'mode')} required>
                <ChoiceButtons
                  layout="chips"
                  value={mode}
                  onChange={setMode}
                  options={['cash', 'card', 'bank_transfer', 'mobile_wallet', 'online_gateway'].map((m) => ({ value: m, label: humanize(m, 'payment_mode') }))}
                />
              </Field>
              {mode === 'cash' ? (
                <Field label={tr('billing', 'cash_tendered')} hint={tr('billing', 'optional')} error={tenderedProblem ?? undefined}>
                  <Input type="number" step="0.01" min={0} value={tendered} onChange={(e) => setTendered(e.target.value)} className="tabular" />
                </Field>
              ) : (
                <Field label={tr('billing', 'reference')}>
                  <Input value={txnRef} onChange={(e) => setTxnRef(e.target.value)} />
                </Field>
              )}
            </div>
            {settles.length > 0 && (
              <div className="rounded-md bg-muted/50 p-2 text-sm">
                <p className="font-medium">{tr('billing', 'this_payment_settles')}</p>
                <ul className="mt-1 space-y-0.5">
                  {settles.map((row) => (
                    <li key={row.id}>
                      {row.amountMinor >= row.balanceMinor
                        ? tr('billing', 'settles_x_in_full', { number: row.number, amount: money(row.amountMinor) })
                        : tr('billing', 'settles_x_in_part', { number: row.number, amount: money(row.amountMinor), balance: money(row.balanceMinor) })}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {error && <Alert variant="destructive">{error}</Alert>}
            <Button
              onClick={() => {
                setError(null);
                setAttempted(true);
                if (amountProblem || tenderedProblem) return;
                collect.mutate();
              }}
              loading={collect.isPending}
            >
              {tr('billing', 'record_payment')}
            </Button>
          </div>
        )}
      </div>
    </Dialog>
  );
}
